/**
 * @voltxampere/components — Zener diode dedicated component (Sprint 23 B2)
 *
 * Sprint 22'ye kadar Zener, `SCHEMA_SYMBOL_ALIASES.zener = "diode"`
 * üzerinden Si-diyot olarak çalışıyordu — breakdown davranışı YOK,
 * sadece forward bias. Sprint 23 B2 Zener'i dedicated component'a
 * yükseltir; SPICE Zener modeli: standart Shockley forward + reverse
 * breakdown branch.
 *
 * Matematiksel (single equation, smooth):
 *
 *   I_d(V_d) = Is · (exp(V_d/(n·Vt)) - 1)              ← forward
 *            − Ibv · (exp(-(V_d + Vz)/(n_z·Vt)) - 1)   ← breakdown
 *
 * Forward bias (V_d ≫ 0): birinci terim Shockley dominant.
 * Reverse bias V_d > -Vz: hem terim küçük, akım ≈ -Is (leakage).
 * Breakdown V_d < -Vz: ikinci terim dominant, akım dramatic artar.
 *
 * `Vz` (breakdown voltage), `Iz` (breakdown current at the knee),
 * `Nz` (breakdown ideality factor) Sprint 23 B2 yeni parametreler;
 * `Is`/`N` standart diyot parametreleri ile aynı (varsayılanlar
 * Si diyot ile birebir).
 *
 * Sprint 22 D2'nin junction-cap parametreleri (Cj0, Vj, M, Tt, FC)
 * Zener için de geçerli — additive, zero-default.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
export const ZenerParamsSchema = z
    .object({
    /** Saturation current `Is` (A); default Si diode 1e-14. */
    saturationCurrent: z.number().positive().finite().default(1e-14),
    /** Forward ideality factor `n`; default 1 (Si Shockley). */
    ideality: z.number().positive().finite().default(1),
    /** Optional series resistance for non-ideal Zener. */
    seriesResistance: z.number().nonnegative().finite().optional(),
    /**
     * Breakdown voltage `Vz` (V), positive. Default 5.1 V — yaygın
     * 1N4733 5.1 V Zener referans noktası.
     */
    breakdownVoltage: z.number().positive().finite().default(5.1),
    /**
     * Breakdown knee current `Iz` (A) — typical zener test akımı,
     * datasheet'lerde "I_ZT" olarak geçer. Default 5 mA (1N4733
     * specifies I_ZT = 49 mA at 5.1 V; küçük signal Zener'lerde 5 mA
     * yaygındır — kullanıcı override eder).
     */
    breakdownCurrent: z.number().positive().finite().default(5e-3),
    /**
     * Breakdown ideality factor `Nz`. Tipik 1-1.5; Avalanche
     * breakdown Zener'lerinde ~1, Tunneling Zener'lerinde 1.5+.
     * Default 1.
     */
    breakdownIdeality: z.number().positive().finite().default(1),
    /** Sprint 22 D2 junction-cap parameters (zero-default, additive). */
    Cj0: z.number().nonnegative().finite().optional(),
    Vj: z.number().positive().finite().optional(),
    M: z.number().positive().finite().optional(),
    Tt: z.number().nonnegative().finite().optional(),
    FC: z.number().min(0).lt(1).optional(),
})
    .strict();
export const zenerDefinition = {
    type: "zener",
    category: "active",
    displayName: { tr: "Zener Diyot", en: "Zener Diode" },
    symbol: "Z",
    pins: [
        { id: "anode", side: "left", offset: 0, label: "A" },
        { id: "cathode", side: "right", offset: 0, label: "K" },
    ],
    parameterSchema: ZenerParamsSchema,
    nonlinear: true,
};
//# sourceMappingURL=zener.js.map